import { api, handleApiError } from './api';
import dayjs from 'dayjs'; // Needed for date formatting if passed to functions
import type {
    SessionData as DashboardSessionAnalyticsData, // Renamed to avoid conflict
    InteractionData
} from '../types/analytics'; // Assuming these types are defined in ../types/analytics.ts

// Define specific params types if needed, e.g.:
export interface AnalyticsParams { // Exported for use in components
    projectId?: string; // Made optional for "All Projects" cases
    businessId?: string; // For business-level aggregations
    startDate?: string; // Expecting ISO string format
    endDate?: string;   // Expecting ISO string format
    timeframe?: 'daily' | 'weekly' | 'monthly';
    limit?: number;
    type?: string; // Add type field
    // Add other potential params like customerId, etc.
}


// --- Interfaces for API responses based on Dashboard.tsx static data ---

// For projectSessionData (SessionOverview on Dashboard)
export interface SessionOverviewData {
  totalSessions: number;
  data: Array<{ month: string; count: number }>;
}

// For projectAnalyticsData.topObjections
export interface TopObjectionData {
  objection: string;
  count: number;
}

// For projectQuestionsData
export interface TopQuestionData {
  question: string;
  percentage: number; // Note: aggregations/routes.py returns count, not percentage directly
  count: number;
}

// For projectDetailedData.activeTime (already covered by /sessions/active-time)
// For projectDetailedData.userSessions
export interface UserSessionData {
  date: string; // e.g., "Mon", "Tue"
  newUsers: number;
  returningUsers: number;
}

// For projectDetailedData.sessionOverTime
export interface SessionOverTimeData {
  date: string; // e.g., "2025-02-01"
  count: number;
  duration: number; // Average duration or total? Dashboard implies count and avg duration
}

// For SOPDeviationCard, DealSentimentCard, CallQualityCard (summary/overall metrics)
// These are very generic; actual API response will dictate structure.
// Assuming a simple structure for now for summarization.
export interface MetricSummary {
    averageScore?: number;
    deviationPercentage?: number; // For SOP
    positiveTrend?: number;
    negativeTrend?: number;
    totalAnalyzed?: number;
    // ... other summary fields
}

// --- Interface for ChartVisualization.tsx ---
export interface ChartDataPoint {
  name: string;
  location: string;
  metrics: {
    "SOP Deviation": number;
    "Deal Sentiment": number;
    "Call Quality": number;
  };
  logo: string;
}
// --- End of Interface for ChartVisualization.tsx ---

const PYTHON_BACKEND_HOST = "https://staging.conversation.convrsespaces.com";

class AppAnalyticsService {

    async getActiveTime(params: AnalyticsParams): Promise<DashboardSessionAnalyticsData['activeTime']> {
        try {
            // Endpoint from sales-app-analytics.routes.js: /sessions/active-time
            const response = await api.get('/sessions/active-time', { params });
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    async getBounceRate(params: AnalyticsParams): Promise<DashboardSessionAnalyticsData['bounceRate']> {
        try {
            // Endpoint from sales-app-analytics.routes.js: /sessions/bounce-rate
            const response = await api.get('/sessions/bounce-rate', { params });
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    async getFrequency(params: AnalyticsParams): Promise<DashboardSessionAnalyticsData['frequency']> {
        try {
            // Endpoint from sales-app-analytics.routes.js: /sessions/frequency
            const response = await api.get('/sessions/frequency', { params });
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }
    
    // --- New methods for Dashboard data ---

    async getSessionOverview(params: AnalyticsParams): Promise<SessionOverviewData> {
        try {
            const response = await api.get<SessionOverviewData>('/api/dashboard/session-overview', { params });
            return response.data;
        } catch (error) {
            console.error("Error in getSessionOverview:", error);
            return Promise.resolve({
                totalSessions: 0,
                data: []
            });
        }
    }

    async getTopObjectionsForProject(params: AnalyticsParams): Promise<TopObjectionData[] | { categories: Array<{category: string, count: number, percentage: number, objections: Array<{objection_text: string, count: number}>}> }> {
        try {
            let endpoint = '';
            if (params.businessId) {
                endpoint = `${PYTHON_BACKEND_HOST}/aggregations/business/${params.businessId}/top_objections_summary`;
            } else {
                endpoint = `${PYTHON_BACKEND_HOST}/aggregations/overall/top_objections_summary`;
            }
            
            const response = await api.get(endpoint);
            console.log('Raw API response for objections:', response.data);
            
            // Check if response has the new categorized structure
            if (response.data && response.data.categories && Array.isArray(response.data.categories)) {
                // Return the new categorized structure
                console.log('Returning categorized structure');
                return response.data;
            }
            
            // Fallback to old structure
            if (response.data && response.data.objections_by_text) {
                console.log('Returning old structure (objections_by_text)');
                return response.data.objections_by_text.map((obj: {objection: string, count: number}) => ({
                    objection: obj.objection,
                    count: obj.count
                }));
            }
            
            console.log('No valid data found, returning empty array');
            return [];
        } catch (error) {
            console.error("Error in getTopObjectionsForProject:", error);
            return Promise.resolve([]); // Return empty array on error to prevent UI breakage
        }
    }
    
    async getTopQuestionsForProject(params: AnalyticsParams): Promise<TopQuestionData[]> {
        try {
            let endpoint = '';
            if (params.businessId) {
                endpoint = `${PYTHON_BACKEND_HOST}/aggregations/business/${params.businessId}/top_questions_summary`;
            } else {
                endpoint = `${PYTHON_BACKEND_HOST}/aggregations/overall/top_questions_summary`;
            }
            
            const response = await api.get<{business_identifier?: string, questions_by_category: Array<{category: string, count: number}>}>(endpoint);
            if (response.data && response.data.questions_by_category) {
                return response.data.questions_by_category.map(q => ({
                    question: q.category,
                    count: q.count,
                    percentage: 0 
                }));
            }
            return [];
        } catch (error) {
            console.error("Error in getTopQuestionsForProject:", error);
            return Promise.resolve([]);
        }
    }

    async getUserSessions(params: AnalyticsParams): Promise<UserSessionData[]> {
        try {
            // No direct endpoint in provided files. This would be a new API.
            // Placeholder for /api/dashboard/user-sessions
            const response = await api.get<UserSessionData[]>('/api/dashboard/user-sessions', { params });
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    async getSessionOverTime(params: AnalyticsParams): Promise<SessionOverTimeData[]> {
        try {
            // Use existing /sessions/active-time endpoint from sales-app-analytics.routes.js
            // Ensure params sent include timeframe: 'daily' or as needed by dashboard
            const effectiveParams = { ...params, timeframe: params.timeframe || 'daily' };
            const response = await api.get<Array<{ _id: string; sessionCount: number; avgSessionDuration: number; totalActiveTime: number }>>(
                '/sessions/active-time', 
                { params: effectiveParams }
            );
            
            // Transform the response to match SessionOverTimeData structure
            if (response.data && Array.isArray(response.data)) {
                return response.data.map(item => ({
                    date: item._id, // _id from API is the date string
                    count: item.sessionCount,
                    duration: Math.round(item.avgSessionDuration / (1000 * 60)) // Convert ms to minutes, or use as is if dashboard expects ms
                }));
            }
            return [];
        } catch (error) {
            return handleApiError(error);
        }
    }
    
    // --- Methods for individual metric cards (SOP, Sentiment, Quality) ---
    // These would typically hit aggregation endpoints for a business/project or overall.

    async getSopDeviationSummary(params: AnalyticsParams): Promise<MetricSummary> {
        try {
            if (!params.businessId) {
                console.warn("getSopDeviationSummary: businessId is required for SOP deviation summary");
                return Promise.resolve({ totalAnalyzed: 0, deviationPercentage: 0 });
            }
            
            // TODO: Backend endpoint for SOP deviation summary is not yet implemented.
            // Example: /aggregations/business/{businessId}/sop_deviation_summary
            // const endpoint = `${PYTHON_BACKEND_HOST}/aggregations/business/${params.businessId}/sop_deviation_summary`;
            // const response = await api.get<MetricSummary>(endpoint);
            // return response.data;
            console.warn("getSopDeviationSummary: Backend endpoint not implemented. Returning default MetricSummary.");
            return Promise.resolve({ totalAnalyzed: 0, deviationPercentage: 0 });
        } catch (error) {
            console.error("Error in getSopDeviationSummary:", error);
            return Promise.resolve({ totalAnalyzed: 0, deviationPercentage: 0 });
        }
    }

    async getDealSentimentSummary(params: AnalyticsParams): Promise<MetricSummary> {
        try {
            if (!params.businessId) {
                console.warn("getDealSentimentSummary: businessId is required for deal sentiment summary");
                return Promise.resolve({ totalAnalyzed: 0, averageScore: 0 });
            }
            
            const endpoint = `${PYTHON_BACKEND_HOST}/aggregations/business/${params.businessId}/deal_sentiment_summary`;
            const response = await api.get<MetricSummary>(endpoint);
            return response.data;
        } catch (error) {
            console.error("Error in getDealSentimentSummary:", error);
            return Promise.resolve({ totalAnalyzed: 0, averageScore: 0 });
        }
    }

    async getCallQualitySummary(params: AnalyticsParams): Promise<MetricSummary> {
        try {
            if (!params.businessId) {
                console.warn("getCallQualitySummary: businessId is required for call quality summary");
                return Promise.resolve({ totalAnalyzed: 0, averageScore: 0 });
            }
            
            // TODO: Backend endpoint for call quality summary is not yet implemented.
            // Example: /aggregations/business/{businessId}/call_quality_summary
            // const endpoint = `${PYTHON_BACKEND_HOST}/aggregations/business/${params.businessId}/call_quality_summary`;
            // const response = await api.get<MetricSummary>(endpoint);
            // return response.data;
            console.warn("getCallQualitySummary: Backend endpoint not implemented. Returning default MetricSummary.");
            return Promise.resolve({ totalAnalyzed: 0, averageScore: 0 });
        } catch (error) {
            console.error("Error in getCallQualitySummary:", error);
            return Promise.resolve({ totalAnalyzed: 0, averageScore: 0 });
        }
    }

    async getProjectComparisonChartData(params: AnalyticsParams): Promise<ChartDataPoint[]> {
        try {
            // TODO: Backend endpoints for project comparison chart data are not yet implemented.
            // Examples:
            // /aggregations/projects/comparison-chart
            // /aggregations/business/{businessId}/projects/comparison-chart
            // /aggregations/project/{projectId}/comparison-data
            // let endpoint = `${PYTHON_BACKEND_HOST}/aggregations/projects/comparison-chart`;
            // if (params.businessId) {
            //     endpoint = `${PYTHON_BACKEND_HOST}/aggregations/business/${params.businessId}/projects/comparison-chart`;
            // } else if (params.projectId) {
            //     endpoint = `${PYTHON_BACKEND_HOST}/aggregations/project/${params.projectId}/comparison-data`;
            // }
            // const response = await api.get<ChartDataPoint[]>(endpoint, { params });
            // return response.data;
            console.warn("getProjectComparisonChartData: Backend endpoint not implemented. Returning empty array.");
            return Promise.resolve([]);
        } catch (error) {
            // console.error("Error fetching project comparison chart data:", error); // Original logging
            // return handleApiError(error); // Original error handling
            console.error("Error in getProjectComparisonChartData (expected if backend endpoints are missing):", error);
            return Promise.resolve([]); // Return empty array on error
        }
    }

    // --- Methods from original appAnalyticsService that might be useful or need adjustment ---
    async getInteractionDistribution(params: AnalyticsParams): Promise<{ overview: InteractionData['overview'], byType: InteractionData['byType'] }> {
        try {
            const response = await api.get('/interactions/distribution', { params });
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    async getInteractionSequences(params: AnalyticsParams): Promise<InteractionData['sequences']> {
        try {
            const response = await api.get('/interactions/sequences', { params });
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    async getAllInteractions(params: any): Promise<any> {
        try {
            // `params` is the customerId string. Pass it as a proper query-param
            // object — previously it was both interpolated into the URL AND passed
            // as the axios `params` config, which serialized the string character
            // by character (?0=C&1=U&2=S...), bloating the request.
            const response = await api.get('/sales/customer/journey', {
                params: { customerId: params }
            });
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }
    async getMissedInteractions(projectId: any): Promise<any> {
        try {
            const response = await api.get(`/api/projects/interaction-list/${projectId}`);
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }
        async getCombinedInteractions({clientId, project}: any): Promise<any> {
        try {
        const [allInteractions, missedInteractions] = await Promise.all([
            this.getAllInteractions(clientId),
            this.getMissedInteractions(project)
        ]);
        return { allInteractions, missedInteractions };
        } catch (error) {
        return {
            allInteractions: handleApiError(error),
            missedInteractions: handleApiError(error)
        };
        }
    }
    // --- New Floorselector Analytics Endpoints (Phase 3) ---

    async getTimePerModule(params: { projectId?: string; startDate?: string; endDate?: string }): Promise<any> {
        try {
            const response = await api.get('/interactions/time-per-module', {
                params: { project_id: params.projectId, startDate: params.startDate, endDate: params.endDate }
            });
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    async getMissedModules(params: { projectId?: string; startDate?: string; endDate?: string }): Promise<any> {
        try {
            const response = await api.get('/interactions/missed-modules', {
                params: { project_id: params.projectId, startDate: params.startDate, endDate: params.endDate }
            });
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    async getUtilizationTrend(params: { projectId?: string; startDate?: string; endDate?: string; groupBy?: string }): Promise<any> {
        try {
            const response = await api.get('/interactions/utilization-trend', {
                params: { project_id: params.projectId, startDate: params.startDate, endDate: params.endDate, group_by: params.groupBy || 'month' }
            });
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }
}

export const appAnalyticsService = new AppAnalyticsService();